import { useCallback, useEffect, useMemo, useState } from "react";
import { useAdvertisingData } from "./useAdvertisingData";
import { useAdvertisingFilters } from "./useAdvertisingFilters";

export interface InterdependentOptions {
  brands: string[];
  asins: string[];
  keywords: string[];
  campaigns: string[];
}

export interface UseInterdependentAdvertisingFiltersReturn {
  startDate: Date | null;
  endDate: Date | null;
  selected: {
    brand: string[];
    skus: string[];
    keyword: string[];
    campaign: string[];
  };
  options: InterdependentOptions;
  expectedCount: number;
  isLoading: boolean;
  error: string | null;
  activationOrder: Array<"brand" | "skus" | "keyword" | "campaign">;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  setBrand: (values: string[]) => void;
  setSkus: (values: string[]) => void;
  setKeyword: (values: string[]) => void;
  setCampaign: (values: string[]) => void;
  activate: (name: "brand" | "skus" | "keyword" | "campaign") => void;
  clear: () => void;
  clearFromLevel: (level: number) => void;
}

function parseBrandFromCampaignName(campaignName: string | null | undefined): string | null {
  if (!campaignName) return null;

  const parts = String(campaignName)
    .split("|")
    .map((p) => p.trim());



  for (let i = 1; i < Math.min(parts.length, 4); i++) {
    const candidate = parts[i];

    if (!candidate || candidate === "") continue;

    if (
      candidate.length >= 2 &&
      !/^\d+$/.test(candidate) && 
      !/^B0[A-Z0-9]{8}$/i.test(candidate) && 
      !/^[A-Z]{2,4}$/.test(candidate) && 
      !/^(ST|SP|SB|SD|CAT|KW|COMP|AUTO|MANUAL)$/i.test(candidate) 
    ) {
      return candidate;
    }
  }

  return null;
}

function extractProductAsinFromCampaign(campaignName: string | null | undefined): string | null {
  if (!campaignName) return null;

  const parts = String(campaignName)
    .split("|")
    .map((p) => p.trim());


  for (let i = parts.length - 1; i >= 0; i--) {
    const candidate = parts[i];

    if (!candidate) continue;

    const asinMatch = candidate.match(/\b(B0[A-Z0-9]{8})\b/i);

    if (asinMatch) {
      const asin = asinMatch[1].toUpperCase();
      return asin;
    }
  }

  return null;
}



export function useInterdependentAdvertisingFilters(): UseInterdependentAdvertisingFiltersReturn {
  const {
    startDate: initialStartDate,
    endDate: initialEndDate,
    brand: initialBrand,
    skus: initialSkus,
    keyword: initialKeyword,
    campaign: initialCampaign,
    setStartDate: setInitialStartDate,
    setEndDate: setInitialEndDate,
    setBrand: setInitialBrand,
    setSkus: setInitialSkus,
    setKeyword: setInitialKeyword,
    setCampaign: setInitialCampaign,
    clearFilters: clearInitialFilters,
  } = useAdvertisingFilters();

  // Internal state for interdependent filters, initialized from useAdvertisingFilters
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [brand, setBrand] = useState(initialBrand);
  const [skus, setSkus] = useState(initialSkus);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [campaign, setCampaign] = useState(initialCampaign);

  // Effect to update internal state when initial filters from useAdvertisingFilters change
  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    setBrand(initialBrand);
    setSkus(initialSkus);
    setKeyword(initialKeyword);
    setCampaign(initialCampaign);
  }, [initialStartDate, initialEndDate, initialBrand, initialSkus, initialKeyword, initialCampaign]);


  const { raw, isLoading: dataLoading, error } = useAdvertisingData(10000, startDate, endDate);

  const [activationOrder, setActivationOrder] = useState<
    Array<"brand" | "skus" | "keyword" | "campaign">
  >([]);
  const activate = useCallback((name: "brand" | "skus" | "keyword" | "campaign") => {
    setActivationOrder((prev) => {
      const newOrder = prev.includes(name) ? prev : [...prev, name];
      console.log("🎯 Filter Activated:", { name, prevOrder: prev, newOrder });
      return newOrder;
    });
  }, []);

  const precomputed = useMemo(() => {
    const columns = raw?.columns || [];
    const data = raw?.data || [];
    const colIndex = (name: string) => columns.indexOf(name);
    const idxCampaignName = colIndex("campaign_name");
    const idxKeyword = colIndex("keyword");
    const idxSku = colIndex("sku");
    const idxStartDate = colIndex("start_date");
    const idxEndDate = colIndex("end_date");
    const idxPartitionStart = colIndex("partition_start");
    const idxPartitionEnd = colIndex("partition_end");

    const parseDate = (val: any): Date | null => {
      if (!val) return null;
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    };

    const rows = data.map((row, i) => {
      const campaignName: string | null = idxCampaignName >= 0 ? row[idxCampaignName] : null;
      const brandValue = parseBrandFromCampaignName(campaignName);
      const keywordValue: string | null = idxKeyword >= 0 ? row[idxKeyword] : null;
      const skuFromCol: string | null = idxSku >= 0 ? row[idxSku] : null;
      const start =
        idxPartitionStart >= 0
          ? row[idxPartitionStart]
          : idxStartDate >= 0
            ? row[idxStartDate]
            : null;
      const end =
        idxPartitionEnd >= 0 ? row[idxPartitionEnd] : idxEndDate >= 0 ? row[idxEndDate] : null;
      const startDt = parseDate(start);
      const endDt = parseDate(end);

      const asins = new Set<string>();

      const campaignAsin = extractProductAsinFromCampaign(campaignName);
      if (campaignAsin) asins.add(campaignAsin);

      if (skuFromCol && /^B0[A-Z0-9]{8}$/i.test(skuFromCol)) {
        asins.add(skuFromCol.toUpperCase()); 
      }

      return {
        i,
        brand: brandValue,
        campaign: campaignName || null,
        keyword: keywordValue,
        asins: Array.from(asins),
        primarySku:
          skuFromCol && /^B0[A-Z0-9]{8}$/i.test(skuFromCol) ? skuFromCol.toUpperCase() : null,
        startDate: startDt,
        endDate: endDt,
      };
    });

    // Build inverted indices
    const brandToRows = new Map<string, Set<number>>();
    const skuToRows = new Map<string, Set<number>>();
    const keywordToRows = new Map<string, Set<number>>();
    const campaignToRows = new Map<string, Set<number>>();

    for (const r of rows) {
      if (r.brand) {
        if (!brandToRows.has(r.brand)) brandToRows.set(r.brand, new Set());
        brandToRows.get(r.brand)!.add(r.i);
      }
      if (r.campaign) {
        if (!campaignToRows.has(r.campaign)) campaignToRows.set(r.campaign, new Set());
        campaignToRows.get(r.campaign)!.add(r.i);
      }
      if (r.keyword) {
        if (!keywordToRows.has(r.keyword)) keywordToRows.set(r.keyword, new Set());
        keywordToRows.get(r.keyword)!.add(r.i);
      }
      for (const a of r.asins) {
        if (!skuToRows.has(a)) skuToRows.set(a, new Set());
        skuToRows.get(a)!.add(r.i);
      }
    }

    const allBrands = Array.from(brandToRows.keys());
    const allSkus = Array.from(skuToRows.keys());
    const allKeywords = Array.from(keywordToRows.keys());
    const allCampaigns = Array.from(campaignToRows.keys());



    return {
      rows,
      brandToRows,
      skuToRows,
      keywordToRows,
      campaignToRows,
      allBrands,
      allSkus,
      allKeywords,
      allCampaigns,
    };
  }, [raw]);

  const effectiveRowSet = useMemo(() => {
    const { brandToRows, skuToRows, keywordToRows, campaignToRows } = precomputed;
    const dimensionSets: Set<number>[] = [];

    const unionFor = (values: string[] | undefined, index: Map<string, Set<number>>) => {
      if (!values || values.length === 0) return null;
      const acc = new Set<number>();
      for (const v of values) {
        const s = index.get(v);
        if (s) s.forEach((x) => acc.add(x));
      }
      return acc;
    };

    const b = unionFor(brand, brandToRows);
    if (b) dimensionSets.push(b);
    const s = unionFor(skus, skuToRows);
    if (s) dimensionSets.push(s);
    const k = unionFor(keyword, keywordToRows);
    if (k) dimensionSets.push(k);
    const c = unionFor(campaign, campaignToRows);
    if (c) dimensionSets.push(c);

    if (dimensionSets.length === 0) return null;

    let result = dimensionSets[0];
    for (let i = 1; i < dimensionSets.length; i++) {
      const next = dimensionSets[i];
      const intersected = new Set<number>();
      result.forEach((id) => {
        if (next.has(id)) intersected.add(id);
      });
      result = intersected;
      if (result.size === 0) break;
    }
    return result;
  }, [precomputed, brand, skus, keyword, campaign]);

  const options = useMemo<InterdependentOptions>(() => {
    const sortAlpha = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" });

    const priorOf = (target: "brand" | "skus" | "keyword" | "campaign") => {
      const idx = activationOrder.indexOf(target);
      if (idx <= 0) return new Set<string>();
      return new Set(activationOrder.slice(0, idx));
    };

    const { rows, allBrands, allSkus, allKeywords, allCampaigns } = precomputed;
    if (!rows || rows.length === 0) {
      return { brands: [], asins: [], keywords: [], campaigns: [] };
    }

    const collect = (ids: Set<number> | null, pick: (r: any) => string | string[] | null) => {
      const set = new Set<string>();
      const push = (val: string | null) => {
        if (val && val.trim()) set.add(val.trim());
      };
      const target = ids ? Array.from(ids) : rows.map((r) => r.i);
      for (const id of target) {
        const r = rows[id];
        const v = pick(r);
        if (Array.isArray(v)) v.forEach(push);
        else push(v as string | null);
      }
      return Array.from(set).sort(sortAlpha);
    };

    const buildRowSetWithPrior = (
      target: "brand" | "skus" | "keyword" | "campaign"
    ): Set<number> | null => {
      const { brandToRows, skuToRows, keywordToRows, campaignToRows } = precomputed;
      const prior = priorOf(target);
      const dimensionSets: Set<number>[] = [];

      const unionFor = (values: string[] | undefined, index: Map<string, Set<number>>) => {
        if (!values || values.length === 0) return null;
        const acc = new Set<number>();
        for (const v of values) {
          const s = index.get(v);
          if (s) s.forEach((x) => acc.add(x));
        }
        return acc;
      };

      if (prior.has("brand")) {
        const s = unionFor(brand, brandToRows);
        if (s) dimensionSets.push(s);
      }

      if (prior.has("skus")) {
        const s = unionFor(skus, skuToRows);
        if (s) {
          const filtered = new Set<number>();
          s.forEach((id) => {
            const r = precomputed.rows[id];
            if (r && r.asins.some((asin) => skus.includes(asin))) {
              filtered.add(id);
            }
          });
          dimensionSets.push(filtered);
        }
      }

      if (prior.has("keyword")) {
        const s = unionFor(keyword, keywordToRows);
        if (s) dimensionSets.push(s);
      }

      if (prior.has("campaign")) {
        const s = unionFor(campaign, campaignToRows);
        if (s) dimensionSets.push(s);
      }

      if (dimensionSets.length === 0) return null;

      let result = dimensionSets[0];
      for (let i = 1; i < dimensionSets.length; i++) {
        const next = dimensionSets[i];
        const intersected = new Set<number>();
        result.forEach((id) => {
          if (next.has(id)) intersected.add(id);
        });
        result = intersected;
        if (result.size === 0) break;
      }
      return result;
    };

    const rowsForBrand = buildRowSetWithPrior("brand");
    const rowsForSkus = buildRowSetWithPrior("skus");
    const rowsForKeyword = buildRowSetWithPrior("keyword");
    const rowsForCampaign = buildRowSetWithPrior("campaign");

    return {
      brands: rowsForBrand ? collect(rowsForBrand, (r) => r.brand) : [...allBrands].sort(sortAlpha),
      asins: rowsForSkus ? collect(rowsForSkus, (r) => r.asins) : [...allSkus].sort(sortAlpha),
      keywords: rowsForKeyword
        ? collect(rowsForKeyword, (r) => r.keyword)
        : [...allKeywords].sort(sortAlpha),
      campaigns: rowsForCampaign
        ? collect(rowsForCampaign, (r) => r.campaign)
        : [...allCampaigns].sort(sortAlpha),
    };
  }, [precomputed, brand, skus, keyword, campaign, activationOrder]);


  useEffect(() => {
    setActivationOrder((currentOrder) => {
      const newActivationOrder = currentOrder.filter((filterName) => {
        switch (filterName) {
          case "brand":
            return brand && brand.length > 0;
          case "skus":
            return skus && skus.length > 0;
          case "keyword":
            return keyword && keyword.length > 0;
          case "campaign":
            return campaign && campaign.length > 0;
          default:
            return true;
        }
      });

      if (newActivationOrder.length !== currentOrder.length) {
        console.log("🧹 Auto-removing filters from activation order:", {
          oldOrder: currentOrder,
          newOrder: newActivationOrder,
          brand: brand?.length || 0,
          skus: skus?.length || 0,
          keyword: keyword?.length || 0,
          campaign: campaign?.length || 0,
        });
        return newActivationOrder;
      }
      return currentOrder;
    });
  }, [brand, skus, keyword, campaign]);

  const expectedCount = useMemo(() => {
    const total = precomputed.rows?.length || 0;
    if (!effectiveRowSet) return total;
    return effectiveRowSet.size;
  }, [precomputed, effectiveRowSet]);

  const clear = useCallback(() => {
    clearInitialFilters(); 
    setStartDate(null);
    setEndDate(null);
    setBrand([]);
    setSkus([]);
    setKeyword([]);
    setCampaign([]);
  }, [clearInitialFilters]);

  const clearFromLevel = useCallback(
    (level: number) => {
      if (level < 1 || level > activationOrder.length) return;

      const filterToClear = activationOrder[level - 1];
      if (!filterToClear) return;

      const subsequentFilters = activationOrder.slice(level);

      const filtersToClear = [filterToClear, ...subsequentFilters];

      filtersToClear.forEach((filterName) => {
        console.log(`  Clearing filter: ${filterName}`);
        switch (filterName) {
          case "brand":
            setBrand([]);
            setInitialBrand([]); 
            break;
          case "skus":
            setSkus([]);
            setInitialSkus([]); 
            break;
          case "keyword":
            setKeyword([]);
            setInitialKeyword([]); 
            break;
          case "campaign":
            setCampaign([]);
            setInitialCampaign([]); 
            break;
        }
      });

      const newOrder = activationOrder.slice(0, level - 1);
      setActivationOrder(newOrder);
    },
    [activationOrder, setBrand, setSkus, setKeyword, setCampaign, setInitialBrand, setInitialSkus, setInitialKeyword, setInitialCampaign]
  );

  return {
    startDate,
    endDate,
    selected: { brand, skus, keyword, campaign },
    options,
    expectedCount,
    isLoading: dataLoading,
    error: error,
    activationOrder,

    setStartDate: (date) => {
      setStartDate(date);
      setInitialStartDate(date);
      setBrand([]);
      setSkus([]);
      setKeyword([]);
      setCampaign([]);
      setInitialBrand([]);
      setInitialSkus([]);
      setInitialKeyword([]);
      setInitialCampaign([]);
      setActivationOrder([]);
    },
    setEndDate: (date) => {
      setEndDate(date);
      setInitialEndDate(date);
      setBrand([]);
      setSkus([]);
      setKeyword([]);
      setCampaign([]);
      setInitialBrand([]);
      setInitialSkus([]);
      setInitialKeyword([]);
      setInitialCampaign([]);
      setActivationOrder([]);
    },
    setBrand: (values) => { setBrand(values); setInitialBrand(values); },
    setSkus: (values) => { setSkus(values); setInitialSkus(values); },
    setKeyword: (values) => { setKeyword(values); setInitialKeyword(values); },
    setCampaign: (values) => { setCampaign(values); setInitialCampaign(values); },
    activate,
    clear,
    clearFromLevel,
  };
}
